import { useEffect, useRef, useState } from "react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { ShieldCheck } from "lucide-react";
import {
  rememberSessionConsent,
  subscribeGdprConsent,
  type GdprConsentRequest,
} from "@/lib/gdprConsent";
import { supabase } from "@/integrations/supabase/client";

const COPY: Record<
  GdprConsentRequest["context"],
  { title: string; intro: string; actionLabel: string }
> = {
  prospect_create: {
    title: "Confirmation RGPD — Ajout d'un prospect",
    intro:
      "Avant d'enregistrer ce contact, confirmez que vous respectez les obligations du RGPD pour la collecte et le traitement de ses données personnelles.",
    actionLabel: "J'enregistre ce prospect",
  },
  email_send: {
    title: "Confirmation RGPD — Envoi d'un email",
    intro:
      "Avant d'envoyer cet email, confirmez que vous disposez d'une base légale (consentement, intérêt légitime, relation contractuelle) pour contacter le ou les destinataires.",
    actionLabel: "J'envoie en assumant la responsabilité",
  },
  bulk_import: {
    title: "Confirmation RGPD — Import de masse",
    intro:
      "Vous vous apprêtez à importer plusieurs contacts. Confirmez que vous disposez d'une base légale (consentement, intérêt légitime B2B, relation préexistante) pour traiter ces données et que la source est conforme.",
    actionLabel: "J'importe en assumant la responsabilité",
  },
};

export default function GdprConsentHost() {
  const [req, setReq] = useState<GdprConsentRequest | null>(null);
  const [accepted, setAccepted] = useState(false);
  const [remember, setRemember] = useState(false);
  const confirmingRef = useRef(false);

  useEffect(() => subscribeGdprConsent(setReq), []);

  useEffect(() => {
    if (req) {
      confirmingRef.current = false;
      setAccepted(false);
      setRemember(false);
    }
  }, [req]);

  const open = req !== null;
  const copy = req ? COPY[req.context] : null;

  const handleOpenChange = (v: boolean) => {
    if (!v && req && !confirmingRef.current) req.resolve(false);
  };

  const handleConfirm = () => {
    if (!req || !accepted) return;
    confirmingRef.current = true;
    const request = req;
    const remembered = remember;
    if (remember) rememberSessionConsent(req.context);

    request.resolve(true);

    // Audit trail — best effort, never blocks the user.
    void (async () => {
      try {
        const { data } = await supabase.auth.getUser();
        const uid = data.user?.id;
        if (uid) {
          await supabase.from("gdpr_consent_logs").insert({
            coach_id: uid,
            context: request.context,
            user_agent: typeof navigator !== "undefined" ? navigator.userAgent : null,
            metadata: { remembered_for_session: remembered },
          });
        }
      } catch (e) {
        console.warn("[gdprConsent] audit log failed:", e);
      }
    })();
  };

  return (
    <AlertDialog open={open} onOpenChange={handleOpenChange}>
      <AlertDialogContent className="max-w-4xl w-[calc(100%-2rem)] max-h-[90vh] overflow-hidden p-0">
        <div className="grid grid-cols-1 md:grid-cols-2 h-full overflow-y-auto">
          <div className="p-6 flex flex-col gap-4">
            <div className="flex flex-col gap-2 text-center md:text-left">
              <AlertDialogTitle className="flex items-center justify-center md:justify-start gap-2 text-lg font-semibold">
                <ShieldCheck className="h-5 w-5 text-primary shrink-0" />
                {copy?.title}
              </AlertDialogTitle>
              <AlertDialogDescription>{copy?.intro}</AlertDialogDescription>
            </div>

            <div className="rounded-md border border-destructive/40 bg-destructive/5 p-3 text-sm text-foreground/90">
              <div className="text-xs font-semibold uppercase tracking-wide text-destructive mb-1">
                Responsabilité
              </div>
              <p>
                Thiia agit comme simple sous-traitant technique. Vous restez seul
                responsable du traitement et de sa conformité (RGPD, LCEN, ePrivacy).
                <strong>
                  {" "}
                  Thiia ne saurait être tenue responsable d'un envoi ou d'un enregistrement
                  effectué sans base légale ni consentement valable.
                </strong>
              </p>
            </div>

            <label className="flex items-start gap-2 rounded-md border border-border p-3 cursor-pointer hover:bg-muted/30 transition-colors text-sm">
              <Checkbox
                checked={accepted}
                onCheckedChange={(v) => setAccepted(v === true)}
                className="mt-0.5 shrink-0"
              />
              <span>
                Je confirme disposer d'une base légale et j'assume la responsabilité de ce
                traitement conformément au RGPD.
              </span>
            </label>

            <label className="flex items-center gap-2 text-xs text-muted-foreground cursor-pointer">
              <Checkbox
                checked={remember}
                onCheckedChange={(v) => setRemember(v === true)}
              />
              Ne plus afficher cette confirmation pendant cette session
            </label>

            <div className="mt-auto flex flex-row justify-center md:justify-end gap-2 pt-2">
              <AlertDialogCancel onClick={() => req?.resolve(false)}>
                Annuler
              </AlertDialogCancel>
              <AlertDialogAction
                onClick={handleConfirm}
                disabled={!accepted}
                className={!accepted ? "opacity-50 pointer-events-none" : ""}
              >
                {copy?.actionLabel}
              </AlertDialogAction>
            </div>
          </div>

          <div className="p-6 flex flex-col gap-3 bg-muted/20 border-t md:border-t-0 md:border-l border-border overflow-y-auto">
            <div className="rounded-md border border-border bg-background p-3 space-y-2 text-sm">
              <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                Rappel des règles RGPD
              </div>
              <ul className="list-disc pl-5 space-y-1 text-foreground/90">
                <li>
                  <strong>Base légale :</strong> consentement explicite (B2C), intérêt légitime
                  ou relation d'affaires préexistante (B2B).
                </li>
                <li>
                  <strong>Information préalable :</strong> la personne doit savoir qui la
                  contacte, pourquoi, et comment exercer ses droits.
                </li>
                <li>
                  <strong>Droits à respecter :</strong> accès, rectification, opposition,
                  effacement, portabilité.
                </li>
                <li>
                  <strong>Lien de désinscription</strong> obligatoire dans toute prospection
                  par email.
                </li>
              </ul>
            </div>

            <div className="rounded-md border border-border bg-background p-3 text-sm text-foreground/90">
              <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-1">
                Sources de données
              </div>
              <p>
                Thiia s'appuie <strong>exclusivement sur des données publiquement accessibles</strong>
                {" "}(sites web, registres officiels, profils professionnels publics) et n'effectue
                {" "}<strong>aucun scraping de masse</strong>. Les recherches sont déclenchées
                {" "}ponctuellement, à votre demande explicite, pour un usage professionnel ciblé.
              </p>
            </div>

            {req?.context === "prospect_create" && (
              <div className="rounded-md border border-border bg-background p-3 text-sm text-foreground/90">
                <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-1">
                  Données collectées
                </div>
                <p className="mb-2">
                  Lors de l'enregistrement d'un prospect, Thiia peut stocker les catégories de
                  données suivantes, dans la limite de ce que vous saisissez ou importez :
                </p>
                <ul className="list-disc pl-5 space-y-0.5 text-sm">
                  <li><strong>Identité&nbsp;:</strong> prénom, nom, civilité.</li>
                  <li><strong>Coordonnées professionnelles&nbsp;:</strong> email, téléphone, adresse.</li>
                  <li><strong>Informations professionnelles&nbsp;:</strong> entreprise, fonction, secteur, taille, site web.</li>
                  <li><strong>Profils publics&nbsp;:</strong> liens LinkedIn et autres réseaux professionnels.</li>
                  <li><strong>Contexte commercial&nbsp;:</strong> statut, source, notes, tags, historique d'échanges.</li>
                  <li><strong>Métadonnées techniques&nbsp;:</strong> dates de création / mise à jour, identifiant interne.</li>
                </ul>
                <p className="mt-2 text-xs text-muted-foreground">
                  Aucune donnée sensible (santé, opinion, religion, orientation, etc.) ne doit être
                  renseignée. Conservation par défaut&nbsp;: 3 ans après le dernier contact actif.
                </p>
              </div>
            )}
          </div>
        </div>
      </AlertDialogContent>
    </AlertDialog>
  );
}
